import { useCallback, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/apiClient";
import { useAuth } from "@/lib/AuthContext";
import { toast } from "sonner";

/**
 * Wraps `POST /api/base44/sync` so any page that already has a refresh hook
 * can offer a one-click "pull fresh from Base44" action.
 *
 * Returns:
 *   • runSync(onAfter?) → async function. Triggers sync, toasts the result,
 *     and (if successful + onAfter provided) calls onAfter() to refetch.
 *   • syncing → boolean flag for spinner state.
 *   • canSync → true only for admin/manager users (matches the /sync route).
 *
 * If `canSync` is false, `runSync` is a no-op — caller can hide the button.
 */
export default function useBase44Sync() {
  const { user } = useAuth();
  const [syncing, setSyncing] = useState(false);
  const canSync = user?.role === "admin" || user?.role === "manager";

  const runSync = useCallback(async (onAfter) => {
    if (!canSync) return;
    setSyncing(true);
    try {
      const { data } = await api.post("/base44/sync");
      const pulled = data?.pulled;
      toast.success(
        typeof pulled === "number"
          ? `Synced from Base44 · ${pulled} ${pulled === 1 ? "order" : "orders"} pulled`
          : "Synced from Base44"
      );
      if (typeof onAfter === "function") await onAfter();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) || "Base44 sync failed"
      );
    } finally {
      setSyncing(false);
    }
  }, [canSync]);

  return { runSync, syncing, canSync };
}
